/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from "react";
import { APIContext } from "../../api";
import { useParams, useNavigate, Link } from "react-router-dom";
import profilePhoto from "../../assets/profile_photo.svg";
import { PulseLoader } from "react-spinners";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import HelmetLayout from "../../components/HelmetLayout";
import {
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
  Input,
  Box,
} from "@chakra-ui/react";
import { getGravatarUrl, remove_prefix } from "../../utils";
import AdminLoginLink from "./login-link";
import { trimStart } from "lodash";
import AdminChangePlan from "./change-plan";
import AdminDeleteUser from "./delete-user";
import TimeAgo from "../../components/ui-kit/time-ago";
import UserAddons from "../../components/user-addons";
import AdminChangePlanAddons from "./change-addon";
import AppSumoBannerToggle from "./appsumo-banner-toggle";
import UserAiUsage from "./UserAiUsage";
import UserAiDefault from "./UserAiDefault";
import UserAcquisition, { acquisitionFor } from "./UserAcquisition";
import { UserRound, Globe, ExternalLink, ShieldCheck } from "lucide-react";
import { PLANS } from "../../plans";

// Beta programs configuration
const BETA_PROGRAMS = [
  { key: "ai-site-editor", label: "AI Site Editor (beta)" },
  { key: "mail", label: "Mail (beta)" },
  { key: "app-store", label: "App Store (beta)" },
  { key: "commerce", label: "Commerce (beta)" },
  // Lets the user pick the AI builder's adapter and model per prompt (QA).
  { key: "ai-builder-model-picker", label: "AI Builder model picker (beta)" },
  {
    key: "website-builder-tabs",
    label: "Website builder tabs (beta)",
    description: "Show the AI website builder and drag-and-drop editor tabs on any plan. Current plan limits still apply.",
  },
];

function ProjectCard({ project }) {
  return (
    <Link rel="noopener noreferrer"
      className="w-full border border-slate-200 bg-white rounded-lg p-4 hover:border-purple-200 hover:bg-purple-50/40 transition-colors flex items-center justify-between gap-3"
      to={`/projects/${remove_prefix(project?.pk, "project-")}`}
      target="_blank"
    >
      <div className="flex items-center gap-3 min-w-0"><Globe size={18} className="text-[#623ce7] shrink-0" /><h3 className="text-sm font-semibold text-slate-800 truncate">{project?.name || "Untitled website"}</h3></div><ExternalLink size={15} className="text-slate-400 shrink-0" />
    </Link>
  )
}

function UserDetails() {
  const navigate = useNavigate();
  const { sub } = useParams();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const actionButton = "inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-purple-200 transition-colors disabled:opacity-50";
  const [userAttributes, setUserAttributes] = useState({});
  const [accountEnable, setAccountEnable] = useState();
  const [isEmailVerifying, setIsEmailVerifying] = useState(false);
  const [isAccountDisabling, setIsAccountDisabling] = useState(false);
  const [isUserAccountDeleting, setIsUserAccountDeleting] = useState(false);
  const [isUserPasswordResetting, setIsUserPasswordResetting] = useState(false);
  const [changedPassword, setChangePassword] = useState("");
  const [isEditable, setIsEditable] = useState(false);
  const [isUserUpdating, setIsUserUpdating] = useState(false);
  const [accountDetails, setAccountDetails] = useState({});
  const [isChangePlanOpen, setIsChangePlanOpen] = useState(false);
  const [isUserDeleting, setIsUserDeleting] = useState(false);
  const [isAppSumoBannerOpen, setIsAppSumoBannerOpen] = useState(false);
  const [userData, setUserData] = useState({});
  const [betaSignups, setBetaSignups] = useState({});
  const [togglingBetaProgram, setTogglingBetaProgram] = useState(null);
  const {
    getUserAttribute,
    updateUserAttributes,
    getUserEmailVerify,
    userAccountDisable,
    userAccountEnable,
    userDelete,
    resetUserPassword,
    toggleBetaProgram,
  } = useContext(APIContext);
  const [isRequestingLoginLink, setIsRequestingLoginLink] = useState(false);
  const [isChangeAddonOpen, setIsChangeAddonOpen] = useState(false);

  var username = accountDetails?.username;
  const activePlan = accountDetails?.subscription?.status === "active"
    ? PLANS.find(plan => plan.id === accountDetails.subscription.plan)
    : null;

  useEffect(() => {
    let current = true;
    setLoading(true);
    setLoadError(false);
    getUserAttribute(sub)
      .then((res) => {
        if (!current) return;
        setUserAttributes(res?.attributes ?? {});
        setAccountEnable(res?.enabled);
        setAccountDetails(res);
        setUserData(res?.data || {});
        setBetaSignups(res?.betaSignups || {});
        setLoading(false);
      })
      .catch((error) => {
        if (current) { setLoadError(true); setLoading(false); }
      });
    return () => { current = false; };
  }, [sub, retry]);

  var selfSites =
    accountDetails?.projects?.filter((project) => {
      if (project?.members?.[userAttributes?.sub]?.superAdmin) {
        return true;
      }
      return false;
    }) ?? [];

  var otherSites =
    accountDetails?.projects?.filter((project) => {
      if (!project?.members?.[userAttributes?.sub]?.superAdmin) {
        return true;
      }
      return false;
    }) ?? [];

  // Check if user is an AppSumo user
  const isAppSumoUser = accountDetails?.subscription?.platform === "appsumo" || 
                       accountDetails?.subscription?.plan?.startsWith?.("appsumo");

  return (
    <>
      <HelmetLayout title="User Details" />
      <Link className="inline-block mb-4 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm" to={`/enterprise-plans?user=${encodeURIComponent(sub)}`}>Create custom plan</Link>
      {loading && <div role="status" className="flex items-center justify-center py-24"><PulseLoader color="#623ce7" size={10} /></div>}
      {loadError && <div role="alert" className="bg-white border border-red-200 rounded-xl p-6 text-sm text-red-700">Could not load this user. <button className="underline font-semibold" onClick={() => setRetry((value) => value + 1)}>Retry</button></div>}
      {!loading && !loadError && (
        <div className="space-y-6 pb-12">
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <img src={getGravatarUrl(userAttributes.email)} alt="" className="w-14 h-14 rounded-xl border border-slate-200" />
                <div className="min-w-0"><p className="text-xs font-semibold uppercase tracking-wider text-[#623ce7] mb-1">User details</p><h1 className="text-2xl font-bold text-slate-900 break-words">{[userAttributes["custom:given_name"], userAttributes["custom:family_name"]].filter(Boolean).join(" ") || userAttributes.email}</h1><p className="text-sm text-slate-500 break-all">{userAttributes.email}</p></div>
              </div>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${accountEnable ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}><ShieldCheck size={14} />{accountEnable ? "Account enabled" : "Account disabled"}</span>
            </div>
            <UserAcquisition acquisition={acquisitionFor(accountDetails)} />
            <UserAiUsage userID={sub} />
            <UserAiDefault key={sub} userID={sub} />
            {
              isEditable ? (
                <div className="w-full flex justify-between items-center">
                  <h1 className="text-[#121213] text-2xl">Attributes</h1>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setIsEditable(!isEditable);
                        if (isEditable) {
                          if (isUserUpdating) {
                            return;
                          }
                          setIsUserUpdating(true);
                          updateUserAttributes(
                            username,
                            userAttributes["custom:family_name"],
                            userAttributes["custom:given_name"]
                          )
                            .then((res) => {
                              setIsUserUpdating(false);
                              toast.success("User Updated Successfully!", {
                                position: toast.POSITION.TOP_RIGHT,
                              });
                            })
                            .catch((error) => {
                              console.log(error.message);
                              setIsUserUpdating(false);
                            });
                        }
                      }}
                      className="rounded-[20px] bg-[#F5F7FC] px-4 py-2 items-center text-[14px] text-center"
                    >
                      {isUserUpdating ? (
                        <PulseLoader size={8}></PulseLoader>
                      ) : isEditable ? (
                        " Save"
                      ) : (
                        "Edit Cognito Attributes"
                      )}
                    </button>
                  </div>
                </div>
              ) : <></>
            }
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
              <h2 className="text-base font-bold text-slate-900">Account overview</h2>
              {
                [
                  {
                    label: "Name",
                    value: (userAttributes["custom:given_name"] ?? "") + " " + (userAttributes["custom:family_name"] ?? "")
                  },
                  {
                    label: "Subscription Plan",
                    value: PLANS?.find(plan => plan?.id === accountDetails?.subscription?.plan)?.label ?? "Free",
                  },
                  {
                    label: "Subscription Status",
                    value: accountDetails?.subscription?.status ?? "Inactive",
                  },
                  ...(activePlan?.aiCredits != null ? [{
                    label: "Monthly AI credits included",
                    value: activePlan.aiCredits.toLocaleString("en-US"),
                  }] : []),
                  {
                    label: "User Login Status",
                    value: accountEnable ? "Enabled" : "Disabled",
                  },
                  {
                    label: "Last Login Time",
                    value: accountDetails?.lastLoginTime ? <TimeAgo date={new Date(parseInt(accountDetails?.lastLoginTime) * 1000)} /> : "Never",
                  }
                ].map((item, index) => (
                  <div key={index} className="flex gap-2 w-full justify-between items-center">
                    <p className="text-sm font-medium text-slate-500">{item.label}</p>
                    <p className="text-sm font-semibold text-slate-800 capitalize text-right">{item.value}</p>
                  </div>
                ))
              }
            </section>
            <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
              <h2 className="text-base font-bold text-slate-900">Beta access</h2>
              <p className="text-xs text-slate-500">Manage access to upcoming features for this account.</p>
              {
                BETA_PROGRAMS.map((program) => {
                  const isEnabled = betaSignups?.[program.key] === true;
                  const isToggling = togglingBetaProgram === program.key;
                  return (
                    <div key={program.key} className="flex gap-2 w-full justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-slate-700">{program.label}</p>
                        {program.description && <p className="mt-1 text-xs text-slate-500">{program.description}</p>}
                      </div>
                      <button
                        disabled={isToggling}
                        onClick={async () => {
                          try {
                            setTogglingBetaProgram(program.key);
                            const next = !isEnabled;
                            await toggleBetaProgram(sub, program.key, next);
                            setBetaSignups({ ...(betaSignups || {}), [program.key]: next });
                            toast.success(
                              next ? `${program.label} enabled` : `${program.label} disabled`,
                              { position: toast.POSITION.TOP_RIGHT }
                            );
                          } catch (e) {
                            toast.error("Failed to update beta access", { position: toast.POSITION.TOP_RIGHT });
                          } finally {
                            setTogglingBetaProgram(null);
                          }
                        }}
                        aria-pressed={isEnabled}
                        aria-label={`${program.label} access`}
                        className={`min-w-[56px] px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${isEnabled ? "bg-purple-50 border-purple-200 text-[#623ce7]" : "bg-slate-50 border-slate-200 text-slate-500"}`}
                      >
                        {isToggling ? <PulseLoader size={8} /> : (isEnabled ? "On" : "Off")}
                      </button>
                    </div>
                  );
                })
              }
            </section>
            </div>

            {/* User Details */}
            {
              isEditable ? (
                <div className="w-full p-6 bg-white border border-slate-200 shadow-sm rounded-xl">
                  <div className="flex gap-4">
                    <div className="w-4/5 pr-4">
                      {Object.entries(userAttributes).map(([key, value], index) => {
                        const keys = [
                          "sub",
                          "email_verified",
                          "email",
                          "name",
                          "identities",
                        ];
                        return (
                          <div key={index} className="mb-4">
                            <label className="text-sm font-medium text-slate-500">
                              {key}
                            </label>
                            <input
                              readOnly={!isEditable || keys.includes(key)}
                              value={value}
                              onChange={(event) =>
                                setUserAttributes({
                                  ...userAttributes,
                                  [key]: event.target.value,
                                })
                              }
                              className="block mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-[#623ce7] focus:outline-none read-only:bg-slate-50"
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="w-1/5 flex items-start justify-end">
                      <img
                        className="w-12 h-auto rounded-full"
                        src={getGravatarUrl(userAttributes.email)}
                        alt="profile-img"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <></>
              )
            }

            {/* Actions Buttons */}

            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
              <h2 className="text-base font-bold text-slate-900 mb-1">Account actions</h2>
              <p className="text-xs text-slate-500 mb-4">Manage sign-in, subscription and account settings.</p>
              <div className="flex flex-wrap gap-2">
              <button
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-lg border border-[#623ce7] bg-[#623ce7] text-white shadow-sm hover:bg-[#512bd4] transition-colors"
                onClick={() => {
                  setIsRequestingLoginLink(true);
                }}
              >
                Login Link
              </button>
              <>
                    <button
                      onClick={() => {
                        setIsChangePlanOpen(true);
                      }}
                      className={actionButton}
                    >
                      Change Plan
                    </button>
                    <button
                      onClick={() => {
                        setIsChangeAddonOpen(true);
                      }}
                      className={actionButton}
                    >
                      Change Addons
                    </button>
                    {isAppSumoUser && (
                      <button
                        onClick={() => {
                          setIsAppSumoBannerOpen(true);
                        }}
                        className={actionButton}
                      >
                        Toggle AppSumo Banner
                      </button>
                    )}
                    <button
                      onClick={onOpen}
                      className={actionButton}
                    >
                      Change Password
                    </button>
                    <button
                      onClick={() => {
                        if (isEmailVerifying) {
                          return;
                        }
                        setIsEmailVerifying(true);
                        getUserEmailVerify(username)
                          .then((res) => {
                            setIsEmailVerifying(false);
                            toast.success("Email is Verified!", {
                              position: toast.POSITION.TOP_RIGHT,
                            });
                          })
                          .catch((error) => {
                            console.error(error.message);
                            setIsEmailVerifying(false);
                          });
                      }}
                      className={actionButton}
                    >
                      {isEmailVerifying ? (
                        <PulseLoader size={8}></PulseLoader>
                      ) : (
                        "Verify User"
                      )}
                    </button>
                    <button
                      onClick={() => {
                        if (isAccountDisabling) {
                          return;
                        }
                        setIsAccountDisabling(true);
                        if (accountEnable) {
                          userAccountDisable(username)
                            .then((res) => {
                              setIsAccountDisabling(false);
                              setAccountEnable(false);
                              toast.success("User is Disabled !", {
                                position: toast.POSITION.TOP_RIGHT,
                              });
                            })
                            .catch((error) => {
                              console.error(error.message);
                              setIsAccountDisabling(false);
                            });
                        } else {
                          userAccountEnable(username)
                            .then((res) => {
                              setIsAccountDisabling(false);
                              setAccountEnable(true);
                              toast.success("User is Enabled!", {
                                position: toast.POSITION.TOP_RIGHT,
                              });
                            })
                            .catch((error) => {
                              console.error(error.message);
                              setIsAccountDisabling(false);
                            });
                        }
                      }}
                      className={actionButton}
                    >
                      {isAccountDisabling ? (
                        <PulseLoader size={8}></PulseLoader>
                      ) : accountEnable ? (
                        " Disable User"
                      ) : (
                        "Enable User"
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setIsUserDeleting(true);
                      }}
                      className={actionButton}
                    >
                      {isUserAccountDeleting ? (
                        <PulseLoader size={8}></PulseLoader>
                      ) : (
                        "Delete User"
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditable(true);
                      }}
                      className={actionButton}
                    >
                      {isEmailVerifying ? (
                        <PulseLoader size={8}></PulseLoader>
                      ) : (
                        "Edit Cognito Attributes"
                      )}
                    </button>
              </>
              </div>
              <Modal
                blockScrollOnMount={false}
                isOpen={isOpen}
                onClose={onClose}
                borderRadius="21px"
              >
                <ModalOverlay />
                <ModalContent>
                  <ModalCloseButton />
                  <ModalHeader mt="20px" textAlign="center" fontSize="24px">
                    Reset User Password
                  </ModalHeader>
                  <ModalBody
                    mx="20px"
                    p="32px"
                    borderRadius="20px"
                    boxShadow="0px 3px 3px rgba(230, 233, 253, 0.8)"
                  >
                    <Text fontWeight="medium" fontSize="16px">
                      Reset Password
                    </Text>
                    <Input
                      type="password"
                      size="md"
                      boxShadow="0px 3px 3px rgba(230, 233, 253, 0.8)"
                      borderRadius="20px"
                      mt="1rem"
                      value={changedPassword}
                      onChange={(event) =>
                        setChangePassword(event.target.value)
                      }
                    />
                  </ModalBody>
                  <ModalFooter>
                    <Box width="100%" display="flex" justifyContent="center">
                      <Box
                        onClick={() => {
                          if (isUserPasswordResetting) {
                            return;
                          }
                          setIsUserPasswordResetting(true);
                          resetUserPassword(
                            userAttributes.email,
                            changedPassword
                          )
                            .then((res) => {
                              setIsUserPasswordResetting(false);
                              toast.success(`User Password Changed!`, {
                                position: toast.POSITION.TOP_RIGHT,
                              });
                              onClose();
                            })
                            .catch((error) => {
                              console.error(error.message);
                              setIsUserPasswordResetting(false);
                            });
                        }}
                        as="button"
                        lineHeight="1.2"
                        px="16px"
                        py="10px"
                        borderRadius="20px"
                        fontSize="16px"
                        fontWeight="normal"
                        bg="#5CBD20"
                        color="#ffffff"
                        _focus={{
                          boxShadow:
                            "0px 70.8569px 51.2194px rgba(41, 72, 152, 0.037963), 0px 42.1092px 27.8569px rgba(41, 72, 152, 0.0303704), 0px 21.8644px 14.2119px rgba(41, 72, 152, 0.025)",
                        }}
                      >
                        Save
                      </Box>
                    </Box>
                  </ModalFooter>
                </ModalContent>
              </Modal>
              <AdminLoginLink
                open={isRequestingLoginLink}
                setOpen={setIsRequestingLoginLink}
                username={username}
              />
              <AdminChangePlan
                open={isChangePlanOpen}
                setOpen={setIsChangePlanOpen}
                subscription={accountDetails?.subscription}
                userAttributes={userAttributes}
                setAccountDetails={setAccountDetails}
              />
              <AdminDeleteUser
                open={isUserDeleting}
                setOpen={setIsUserDeleting}
                username={username}
              />
              <AdminChangePlanAddons
                open={isChangeAddonOpen}
                setOpen={setIsChangeAddonOpen}
                subscription={accountDetails?.subscription}
                userAttributes={userAttributes}
                setAccountDetails={setAccountDetails}
              />
              <AppSumoBannerToggle
                open={isAppSumoBannerOpen}
                setOpen={setIsAppSumoBannerOpen}
                userSub={sub}
                userEmail={userAttributes?.email}
                subscription={accountDetails?.subscription}
                userData={userData}
                onBannerToggled={(showBanner) => {
                  setUserData(prev => ({
                    ...prev,
                    appsumo_review_image_submitted: showBanner
                  }));
                }}
              />
            </div>
            <div className="bg-white w-full border border-slate-200 rounded-xl shadow-sm flex flex-col gap-3 p-6">
              <h2
                className="text-base font-bold text-slate-900 w-full">
                Websites
              </h2>
              {
                selfSites.length > 0 ? (
                  <>
                    {
                      selfSites.map((project, index) => (
                        <ProjectCard key={`self-project-${project?.sk}`} project={project} />
                      ))
                    }
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-400 py-6 text-center">No Websites</p>
                  </>
                )
              }
            </div>
            <div className="bg-white w-full border border-slate-200 rounded-xl shadow-sm flex flex-col gap-3 p-6">
              <h2
                className="text-base font-bold text-slate-900 w-full">
                Shared Websites
              </h2>
              {
                otherSites.length > 0 ? (
                  <>
                    {
                      otherSites.map((project, index) => (
                        <ProjectCard key={`shared-project-${project?.sk}`} project={project} />
                      ))
                    }
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-400 py-6 text-center">No Websites</p>
                  </>
                )
              }
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserDetails;
